import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { DatabaseSoundPack, DatabaseSoundPackModel } from '../services/DatabaseSoundPack';
import { useUser } from './UserContext';

interface SoundPacksContextType {
    soundPacks: DatabaseSoundPack[];
    coverImages: Record<string, string>;
    isLoading: boolean;
    error: string | null;
}

const SoundPacksContext = createContext<SoundPacksContextType>({
    soundPacks: [],
    coverImages: {},
    isLoading: true,
    error: null,
});

export const useSoundPacks = () => useContext(SoundPacksContext);

export const SoundPacksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [soundPacks, setSoundPacks] = useState<DatabaseSoundPack[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { isAdmin: isAdminUser, isLoading: userLoading } = useUser();

    // Cover map mirrors the cached coverImageUrl fields from Firestore.
    const coverImages = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        for (const pack of soundPacks) {
            if (pack.id && pack.coverImageUrl) map[pack.id] = pack.coverImageUrl;
        }
        return map;
    }, [soundPacks]);

    useEffect(() => {
        if (userLoading) return;

        setError(null);
        setIsLoading(true);

        let receivedFirst = false;
        const unsub = DatabaseSoundPackModel.subscribeSoundPacks(
            isAdminUser,
            (packs) => {
                setSoundPacks([...packs].sort(DatabaseSoundPackModel.sortByCreatedAtDesc));
                if (!receivedFirst) {
                    receivedFirst = true;
                    setIsLoading(false);
                }
            },
            (err) => {
                setError(err.message);
                setIsLoading(false);
            },
        );

        return () => unsub();
    }, [userLoading, isAdminUser]);

    const value: SoundPacksContextType = {
        soundPacks,
        coverImages,
        isLoading,
        error,
    };

    return (
        <SoundPacksContext.Provider value={value}>
            {children}
        </SoundPacksContext.Provider>
    );
};

export default SoundPacksContext;
